import _ from 'lodash';
import { AdRequestSourceSpace } from 'core/adRequestSource/AdRequestSource';
import client from './RestClient';
import { numberWithCommas } from 'utils/StringUtil';
import { AxiosInstance } from 'axios';
import i18n from 'i18n';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { SpaceStatus } from 'enum/SpaceStatus';

function wrapSegment(json: any) {
  return {
    value: _.get(json, 'value', '').toString(),
    label: _.get(json, 'label', ''),
    extra: {
      i18nKey: 'limitation.labels.peopleNumberTip',
      value: numberWithCommas(_.get(json, 'count', 0))
    }
  };
}

function wrapSegmentGroup(json: any) {
  const isGroup = _.get(json, 'isGroup', false);
  const options = isGroup
    ? _.get(json, 'options', []).map((json) => wrapSegment(json))
    : [];
  return {
    value: _.get(json, 'value', '').toString(),
    label: _.get(json, 'label', ''),
    options,
    isGroup,
    extra: isGroup
      ? undefined
      : {
          i18nKey: 'limitation.labels.peopleNumberTip',
          value: numberWithCommas(_.get(json, 'count', 0))
        }
  };
}

function wrapOptionsWithGroup(json: any) {
  const isGroup = _.get(json, 'isGroup', false);
  const autoUpdate = _.get(json, 'autoUpdate', false);
  const options = _.get(json, 'options', []).map((json) => {
    return {
      value: _.get(json, 'value', '').toString(),
      label: _.get(json, 'label', '')
    };
  });
  const groupName = _.get(json, 'label', '');
  const label = autoUpdate
    ? `${groupName} ${i18n.t('productGroup.labels.autoUpdate')}`
    : groupName;
  return {
    value: _.get(json, 'value', '').toString(),
    label,
    isGroup: isGroup,
    options: isGroup ? [] : options,
    extra: isGroup ? options : undefined
  };
}

/**
 * Map a server label/value record (optionally nested via `options`) into a
 * SelectOptions tree. Recurses for any depth of children. `options` is only
 * set when present in the input — leaves get `undefined` so callers can
 * distinguish flat from nested entries.
 */
function wrapLabelValue(json: any): SelectOptions {
  // 固定門市（fixOutdoorSpace）：store-options 的每筆 record 可能帶 spaceId，用來
  // 對應「被固定的那一台版位」。只在後端有給時才保留，其他 attribute（regions、
  // administrative-areas…）的 record 沒有這個欄位，不受影響。
  const spaceId = _.get(json, 'spaceId');
  return {
    label: _.get(json, 'label', ''),
    value: _.get(json, 'value', '').toString(),
    ...(spaceId !== undefined && spaceId !== null
      ? { spaceId: spaceId.toString() }
      : {}),
    options: Array.isArray(json.options)
      ? json.options.map((child) => wrapLabelValue(child))
      : undefined
  };
}

function wrapLabelValueWithCount(json: any): any {
  return {
    label: _.get(json, 'label', ''),
    value: _.get(json, 'value', '').toString(),
    extra: numberWithCommas(_.get(json, 'count', 0)),
    options: _.get(json, 'options', []).map((subJson) => {
      return {
        label: _.get(subJson, 'label', ''),
        value: _.get(subJson, 'value', '').toString(),
        extra: numberWithCommas(_.get(subJson, 'count', 0))
      };
    })
  };
}

function wrapSpaceAttribute(
  attributeType: string,
  json: any,
  parentAttribute?: {
    key: string;
    i18n: {
      en: string;
      ['zh-tw']: string;
      ja: string;
    };
  }
): SelectOptions {
  const value = parentAttribute
    ? `${parentAttribute.key}/${json.key}`
    : json.key;
  const i18nKey = `productGroup.space.attributes.${attributeType}.${value}`;
  const enLabel = parentAttribute
    ? `${parentAttribute.i18n.en} ${json.i18n.en}`
    : json.i18n.en;
  const twLabel = parentAttribute
    ? attributeType === 'storeNum'
      ? `${parentAttribute.i18n['zh-tw']}_${json.i18n['zh-tw']}`
      : `${parentAttribute.i18n['zh-tw']} > ${json.i18n['zh-tw']}`
    : json.i18n['zh-tw'];
  const jaLabel = parentAttribute
    ? `${parentAttribute.i18n.ja} ${json.i18n.ja}`
    : json.i18n.ja;
  i18n.addResource('en', 'translation', i18nKey, enLabel);
  i18n.addResource('zh-TW', 'translation', i18nKey, twLabel);
  i18n.addResource('ja', 'translation', i18nKey, jaLabel);
  return {
    label: i18n.t(i18nKey),
    value,
    options: json.child
      ? json.child.map((childJson) =>
          wrapSpaceAttribute(attributeType, childJson, {
            key: json.key,
            i18n: json.i18n
          })
        )
      : undefined
  };
}

export type OutdoorSpaceAttributePath =
  | 'store-options'
  | 'regions'
  | 'administrative-areas'
  | 'place-categories'
  | 'characteristic';

export interface AdRequestSourceWebService {
  getCountries(): Promise<SelectOptions[]>;
  getAdx(): Promise<SelectOptions[]>;
  getSegmentLabel(): Promise<SelectOptions[]>;
  getSSPSpaces(publisherIds?: string): Promise<AdRequestSourceSpace[]>;
  getSSPSpacesByAdType(
    adType: string,
    channel?: string,
    strict?: boolean
  ): Promise<AdRequestSourceSpace[]>;
  getAsiaMaxCountries(): Promise<SelectOptions[]>;
  getAges(): Promise<SelectOptions[]>;
  getGenders(): Promise<SelectOptions[]>;
  getOS(): Promise<SelectOptions[]>;
  getSpaceTypes(): Promise<SelectOptions[]>;
  getTenmaxCategories(): Promise<SelectOptions[]>;
  getArticleCategories(): Promise<SelectOptions[]>;
  getCarriers(): Promise<SelectOptions[]>;
  getDevice(): Promise<SelectOptions[]>;
  getContentLanguages(): Promise<SelectOptions[]>;
  getSegments(advertiserId: number): Promise<SelectOptions[]>;
  getTenmaxSegments(): Promise<SelectOptions[]>;
  getDomains(channel?: string): Promise<SelectOptions[]>;
  getAgencySegments(agencyId: number): Promise<SelectOptions[]>;
  getSpaceAdFormats(): Promise<SelectOptions[]>;
  getSpaceAttributes(
    adFormat: string,
    channelGroupSetting?: ChannelGroupSetting
  ): Promise<{ [key: string]: SelectOptions[] }>;
  getSpaceByAttributesAndChannel(
    valuePrefix: boolean,
    attributes: Record<string, string[]>,
    extraGetter?: (record: any) => string | undefined,
    channelGroupId?: number,
    testMode?: boolean,
    deliverableOnly?: boolean
  ): Promise<SelectOptions[]>;
  getSpacesGroupsOfAdType(
    adType: string,
    adTypeId?: number,
    strict?: boolean
  ): Promise<SelectOptions[]>;
  getStoreNumOptions(
    channelGroupId: number,
    beaconConnectionId: number
  ): Promise<SelectOptions[]>;
  getOutdoorSpaceAttributes(
    path: OutdoorSpaceAttributePath,
    channelGroupId: number,
    testMode?: boolean,
    /** 要涵蓋哪些版位狀態；不給就維持後端原本的範圍（只有啟用中的版位）。 */
    spaceStatuses?: SpaceStatus[]
  ): Promise<SelectOptions[]>;
}

export class RestfulAdRequestSourceWebService implements AdRequestSourceWebService {
  restClient: AxiosInstance;

  constructor(restClient: AxiosInstance = client) {
    this.restClient = restClient;
  }

  pickRecords(response: any) {
    return _.sortBy(_.get(response, 'records', []), ['label']).map(
      (record) => ({
        ...record,
        options: record.options
          ? _.sortBy(record.options, ['label'])
          : record.options
      })
    );
  }
  async getCountries(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/countries/pmax'
    );
    return this.pickRecords(response.data);
  }
  async getAdx(): Promise<SelectOptions[]> {
    const response = await this.restClient.get('/v2/ad-request-source/adx');
    return this.pickRecords(response.data);
  }
  async getSegmentLabel(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/tenmax-segment-labels'
    );
    return this.pickRecords(response.data);
  }
  async getSSPSpaces(publisherIds?: string): Promise<AdRequestSourceSpace[]> {
    const requestUrl = publisherIds
      ? `/v2/ad-request-source/spaces/rmax?publisher_id=${publisherIds}`
      : `/v2/ad-request-source/spaces/rmax`;
    const response = await this.restClient.get(requestUrl);
    return this.pickRecords(response.data)
      .map((json) => {
        return wrapOptionsWithGroup(json);
      })
      .sort((optionsA, optionsB) =>
        optionsA.isGroup && !optionsB.isGroup ? -1 : 1
      );
  }
  async getSSPSpacesByAdType(
    adType: string,
    channel?: string,
    strict?: boolean
  ): Promise<AdRequestSourceSpace[]> {
    let requestUrl = `/v2/ad-request-source/spaces/rmax?adType=${adType}`;
    channel && (requestUrl += `&channel=${channel}`);
    strict && (requestUrl += '&strict=true');
    const response = await this.restClient.get(requestUrl);
    return this.pickRecords(response.data)
      .map((json) => {
        return wrapOptionsWithGroup(json);
      })
      .sort((optionsA, optionsB) =>
        optionsA.isGroup && !optionsB.isGroup ? -1 : 1
      );
  }
  async getAsiaMaxCountries(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/countries/rmax'
    );
    return this.pickRecords(response.data);
  }
  async getAges(): Promise<SelectOptions[]> {
    const response = await this.restClient.get('/v2/ad-request-source/ages');
    return this.pickRecords(response.data);
  }
  async getGenders(): Promise<SelectOptions[]> {
    const response = await this.restClient.get('/v2/ad-request-source/genders');
    return this.pickRecords(response.data).map((json) => {
      return wrapLabelValue(json);
    });
  }
  async getOS(): Promise<SelectOptions[]> {
    const response = await this.restClient.get('/v2/ad-request-source/os');
    return this.pickRecords(response.data);
  }
  async getSpaceTypes(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/space-types'
    );
    return this.pickRecords(response.data);
  }
  async getTenmaxCategories(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/tenmax-categories'
    );
    return this.pickRecords(response.data).map((json) => {
      return wrapLabelValue(json);
    });
  }
  async getCarriers(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/carriers'
    );
    return this.pickRecords(response.data);
  }

  async getContentLanguages(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/content-lang'
    );
    return this.pickRecords(response.data).map((json) => {
      return wrapLabelValue(json);
    });
  }

  async getArticleCategories(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/ad-request-source/article-categories?lang=zh'
    );
    return this.pickRecords(response.data).map((json) =>
      wrapLabelValueWithCount(json)
    );
  }

  async getSegments(advertiserId): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      `/v2/ad-request-source/segments?advertiserId=${advertiserId}`
    );
    return this.pickRecords(response.data).map((json) =>
      wrapSegmentGroup(json)
    );
  }

  async getDevice(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      `/v2/ad-request-source/device-types`
    );
    return this.pickRecords(response.data);
  }

  async getTenmaxSegments(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      `/v2/ad-request-source/tenmax-segments`
    );
    return _.get(response.data, 'records', []).map((json) => {
      return wrapSegmentGroup(json);
    });
  }

  async getDomains(channel?: string): Promise<SelectOptions[]> {
    let requestUrl = `/v2/ad-request-source/domains/asiamax`;
    channel && (requestUrl += `?channel=${channel}`);
    const response = await this.restClient.get(requestUrl);
    return this.pickRecords(response.data)
      .map((json) => {
        return wrapOptionsWithGroup(json);
      })
      .sort((optionsA, optionsB) =>
        optionsA.isGroup && !optionsB.isGroup ? -1 : 1
      );
  }

  async getAgencySegments(agencyId: number): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      `/v2/ad-request-source/ad-agencies/segments/${agencyId}`
    );
    return this.pickRecords(response.data).map((json) =>
      wrapLabelValueWithCount(json)
    );
  }

  async getSpaceAdFormats(): Promise<SelectOptions[]> {
    const { data } = await this.restClient.get(
      `/v2/ad-request-source/space-ad-formats`
    );

    if (!data) {
      return [];
    }

    const i18nKey = `productGroup.space.attributes.titles.${data.key}`;
    i18n.addResource('en', 'translation', i18nKey, data.i18n.en);
    i18n.addResource('zh-TW', 'translation', i18nKey, data.i18n['zh-tw']);
    i18n.addResource('ja', 'translation', i18nKey, data.i18n.ja);
    return data.child.map((json) =>
      _.omit(wrapSpaceAttribute(data.key, json), 'options')
    );
  }

  async getSpaceAttributes(
    adFormat: string,
    channelGroupSetting?: ChannelGroupSetting
  ): Promise<{ [key: string]: SelectOptions[] }> {
    let url = `/v2/ad-request-source/space-attributes?adFormat=${adFormat}`;
    if (channelGroupSetting) {
      url += `&channel=${channelGroupSetting.key}`;
      if (channelGroupSetting.config.id) {
        url += `&channelGroupId=${channelGroupSetting.config.id}`;
      }
    }
    const { data } = await this.restClient.get(url);
    return this.pickRecords(data).reduce((acc, json) => {
      const i18nKey = `productGroup.space.attributes.titles.${json.key}`;
      i18n.addResource('en', 'translation', i18nKey, json.i18n.en);
      i18n.addResource('zh-TW', 'translation', i18nKey, json.i18n['zh-tw']);
      i18n.addResource('ja', 'translation', i18nKey, json.i18n.ja);
      acc[json.key] = json.child
        ? json.child.map((child) => wrapSpaceAttribute(json.key, child))
        : [];
      return acc;
    }, {});
  }

  async getSpaceByAttributesAndChannel(
    valuePrefix: boolean,
    attributesWithAdFormat: Record<string, string[]>,
    extraGetter?: (record: any) => string | undefined,
    channelGroupId?: number,
    testMode = false,
    deliverableOnly = false
  ): Promise<SelectOptions[]> {
    const { data } = await this.restClient.post(
      `/v2/ad-request-source/spaces-with-attributes`,
      {
        channelGroupId,
        attributesWithAdFormat,
        testMode,
        deliverableOnly
      }
    );

    return this.pickRecords(data)
      .map((record) => {
        const value = record.value.replace(/^\\/, '');
        return {
          label: valuePrefix ? `${value},${record.label}` : record.label,
          value,
          extra: extraGetter ? extraGetter(record) : undefined
        };
      })
      .filter((option) => !extraGetter || option.extra !== undefined);
  }

  async getSpacesGroupsOfAdType(
    adType: string,
    adTypeId?: number,
    strict?: boolean
  ): Promise<SelectOptions[]> {
    let url = `/v2/ad-request-source/space-groups?adType=${adType}`;
    adTypeId && (url += `&adTypeId=${adTypeId}`);
    strict && (url += '&strict=true');
    const { data } = await this.restClient.get(url);

    return this.pickRecords(data).map((record) => wrapOptionsWithGroup(record));
  }

  async getStoreNumOptions(
    channelGroupId: number,
    beaconConnectionId: number
  ): Promise<SelectOptions[]> {
    let url = `/v2/ads-beacon/store-device-options?beaconConnectionId=${beaconConnectionId}&channelGroupId=${channelGroupId}`;
    const response = await this.restClient.get(url);
    return response.data.records;
  }

  async getOutdoorSpaceAttributes(
    path: OutdoorSpaceAttributePath,
    channelGroupId: number,
    testMode = false,
    spaceStatuses: SpaceStatus[] = []
  ): Promise<SelectOptions[]> {
    const params = new URLSearchParams({
      channelGroupId: String(channelGroupId),
      testMode: String(testMode)
    });
    // 不帶這個條件時後端維持原本的範圍（只有啟用中的版位），所以空陣列一個參數都不送。
    // 一個值一個參數，與其他多值條件同一種攤法；參數名是後端 @RequestParam 的名字（單數），
    // 拼錯不會報錯，只會被靜默忽略而退回預設範圍。
    spaceStatuses.forEach((status) => params.append('spaceStatus', status));
    const { data } = await this.restClient.get(
      `/v2/ad-request-source/${path}?${params.toString()}`
    );
    return _.get(data, 'records', []).map((record) => wrapLabelValue(record));
  }
}
